import { FunctionComponent } from "react"
import { Flex } from "antd"
import { StatDisplaySet } from "src/pages/DataAssets/components/StatDisplaySet"
import { StatDisplay } from "src/pages/DataAssets/components/StatDisplay"
import { CircularProgress } from "src/global/ui/CircularProgress"
import { Icon, IconTypes } from "src/global/ui/Icon/Icon"
import { theme } from "src/global/ui/themes/theme"
import { ErrorState } from "src/global/ui/error"
import { ApolloError } from "@apollo/client"
import { LoadingState } from "src/global/ui/LoadingState"
import { NavLink, useLocation, useMatch, useResolvedPath } from "react-router-dom"

interface DataHealthCardProps {
  title: string
  tooltip: string
  percentage: number
  progressLabel: string
  linkTo: string
  metricLabel: string
  metricValue: string | number
  metricIcon?: IconTypes
  loading?: boolean
  error?: ApolloError
}

const DEFAULT_SEARCH = "?sort=-lastValidation"

export const DataHealthCard: FunctionComponent<DataHealthCardProps> = ({
  title,
  tooltip,
  percentage,
  progressLabel,
  metricLabel,
  metricValue,
  metricIcon,
  linkTo,
  loading,
  error,
}) => {
  const location = useLocation()
  const fullPath = useResolvedPath(linkTo)
  const selected = !!useMatch(fullPath.pathname)
  const metricIconElement = metricIcon ? (
    <Icon
      name={metricIcon}
      size="16px"
      lineHeight="0"
      color={theme.colors.neutralColorPalette.blacks.colorTextQuaternary}
    />
  ) : undefined

  const search = location.search || DEFAULT_SEARCH

  return (
    <NavLink to={{ pathname: linkTo, search }} style={{ textDecoration: "none" }}>
      <StatDisplaySet title={title} info={tooltip} selected={selected} clickable={true}>
        {loading && (
          <Flex
            style={{
              height: "130px",
              width: "100%",
            }}
          >
            <LoadingState loading={true} absolute={false} />
          </Flex>
        )}

        {!loading && error && (
          <Flex
            style={{
              height: "130px",
              width: "100%",
            }}
          >
            <ErrorState errorMessage="failed to load data" includeTryAgainSubtitle={false} />
          </Flex>
        )}

        {!loading && !error && (
          <Flex justify="space-between" align="center" style={{ width: "100%" }}>
            <CircularProgress value={percentage} label={progressLabel} />
            <StatDisplay label={metricLabel} icon={metricIconElement}>
              <span
                style={{ fontSize: theme.typography.fontSize.hero, fontWeight: theme.typography.fontWeight.semibold }}
              >
                {typeof metricValue === "number" ? metricValue.toLocaleString() : metricValue}
              </span>
            </StatDisplay>
          </Flex>
        )}
      </StatDisplaySet>
    </NavLink>
  )
}
